import { useState } from "react";
import { theme, btnPrimary, btnSecondary } from "../theme";
import { api, apiErrorMessage } from "../api/client";

// "Escalate to risk" on an auditor finding: links the finding to an existing
// risk-register row (PATCH /findings/:id/link-risk, controls:write; the server
// checks the risk belongs to the same organisation). The route links only, so a
// risk that does not exist yet is raised in the risk register first.

export interface LinkedRisk {
  id: string;
  title: string;
  displayId: number | null;
}

interface RiskOption extends LinkedRisk {
  status: string;
}

const riskLabel = (r: LinkedRisk) => `${r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}${r.title}`;

export function FindingRiskLink({ findingId, orgId, linkedRisk, canEdit, onChange, onOpenRisk }: {
  findingId: string;
  /** The client organisation when a consultant acts on its behalf. */
  orgId?: string;
  linkedRisk: LinkedRisk | null;
  canEdit: boolean;
  onChange: (risk: LinkedRisk | null) => void;
  onOpenRisk: (riskId: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [risks, setRisks] = useState<RiskOption[] | null>(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scope = { organizationId: orgId };

  async function startPicking() {
    setPicking(true);
    setError("");
    if (risks) return;
    try {
      const d = await api.get<{ risks: RiskOption[] }>("/risks", scope);
      setRisks(d.risks);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the risk register."));
    }
  }

  async function save(riskId: string | null) {
    setBusy(true);
    setError("");
    try {
      const d = await api.patch<{ linkedRisk: LinkedRisk | null }>(`/findings/${findingId}/link-risk`, { riskId }, scope);
      onChange(d.linkedRisk);
      setPicking(false);
      setChoice("");
    } catch (e) {
      setError(apiErrorMessage(e, "Could not update the risk link."));
    } finally {
      setBusy(false);
    }
  }

  const small: React.CSSProperties = { padding: "4px 10px", fontSize: 11 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11.5 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {linkedRisk ? (
          <>
            <span style={{ color: theme.color.faint }}>Escalated to</span>
            <button type="button" onClick={() => onOpenRisk(linkedRisk.id)}
              style={{ background: "none", border: "none", padding: 0, fontSize: 11.5, fontWeight: 600, color: theme.color.accentText, cursor: "pointer" }}>
              {riskLabel(linkedRisk)}
            </button>
            {canEdit && !picking && (
              <button type="button" disabled={busy} onClick={() => save(null)}
                style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: theme.color.faint, cursor: "pointer", textDecoration: "underline" }}>
                Unlink
              </button>
            )}
          </>
        ) : canEdit && !picking ? (
          <button type="button" onClick={startPicking} style={{ ...btnSecondary, ...small }}>Escalate to risk</button>
        ) : null}
      </div>

      {picking && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Risk to link"
            style={{ padding: "4px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12, maxWidth: 320 }}>
            <option value="">{risks ? (risks.length ? "Choose a risk…" : "No risks recorded yet") : "Loading…"}</option>
            {(risks ?? []).map((r) => <option key={r.id} value={r.id}>{riskLabel(r)}{r.status === "closed" ? " (closed)" : ""}</option>)}
          </select>
          <button type="button" disabled={!choice || busy} onClick={() => save(choice)}
            style={{ ...btnPrimary, ...small, opacity: !choice || busy ? 0.5 : 1 }}>
            {busy ? "Linking…" : "Link"}
          </button>
          <button type="button" onClick={() => { setPicking(false); setChoice(""); }} style={{ ...btnSecondary, ...small }}>Cancel</button>
          <span style={{ color: theme.color.faint }}>No suitable risk? Raise one in the risk register first.</span>
        </div>
      )}
      {error && <div style={{ color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
